import { useRef, useState } from 'react';
import { Box } from '@mui/material';

import type { Json } from '@/integrations/supabase/types';
import type { Block } from '@/types/database';
import { clampSplit } from '@/lib/grid-resize';
import { EditableBlock } from './EditableBlock';

interface GridRowProps {
  blocks: Block[];
  basePath: string;
  canManage: boolean;
  selectedBlockId: string | null;
  isFirstRow: boolean;
  isLastRow: boolean;
  onSelectBlock: (blockId: string) => void;
  onEditBlock: (blockId: string) => void;
  onMoveRowUp: (blockId: string) => void;
  onMoveRowDown: (blockId: string) => void;
  onDeleteBlock: (blockId: string) => void;
  onAddToRow: (afterBlockId: string) => void;
  onResize: (leftBlockId: string, rightBlockId: string, leftSpan: number, rightSpan: number) => void;
  onUpdateBlockContent: (blockId: string, content: Json) => void;
}

interface DragState {
  boundaryIndex: number;
  startX: number;
  leftSpanStart: number;
  rightSpanStart: number;
  leftSpan: number;
  rightSpan: number;
}

// One 12-column grid row. A drag handle sits at each internal boundary
// between adjacent blocks (never at the row's own start/end) — dragging it
// trades columns between just those two neighbors, matching a resizable
// split-pane interaction rather than free-form repositioning.
export function GridRow({
  blocks,
  basePath,
  canManage,
  selectedBlockId,
  isFirstRow,
  isLastRow,
  onSelectBlock,
  onEditBlock,
  onMoveRowUp,
  onMoveRowDown,
  onDeleteBlock,
  onAddToRow,
  onResize,
  onUpdateBlockContent,
}: GridRowProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const startResize = (boundaryIndex: number) => (event: React.PointerEvent) => {
    event.stopPropagation();
    const left = blocks[boundaryIndex];
    const right = blocks[boundaryIndex + 1];
    const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 1;
    const columnWidth = containerWidth / 12;
    const startX = event.clientX;

    setDrag({ boundaryIndex, startX, leftSpanStart: left.col_span, rightSpanStart: right.col_span, leftSpan: left.col_span, rightSpan: right.col_span });

    const handleMove = (moveEvent: PointerEvent) => {
      const deltaColumns = Math.round((moveEvent.clientX - startX) / columnWidth);
      const split = clampSplit(left.col_span, right.col_span, deltaColumns);
      setDrag((current) => (current ? { ...current, leftSpan: split.leftSpan, rightSpan: split.rightSpan } : current));
    };

    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      setDrag((current) => {
        if (current && (current.leftSpan !== current.leftSpanStart || current.rightSpan !== current.rightSpanStart)) {
          onResize(left.id, right.id, current.leftSpan, current.rightSpan);
        }
        return null;
      });
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  return (
    <Box ref={containerRef} sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', position: 'relative' }}>
      {blocks.map((block, index) => {
        const colSpan =
          drag && (drag.boundaryIndex === index || drag.boundaryIndex === index - 1)
            ? drag.boundaryIndex === index
              ? drag.leftSpan
              : drag.rightSpan
            : block.col_span;

        return (
          <Box key={block.id} sx={{ gridColumn: `span ${colSpan}`, position: 'relative' }}>
            <EditableBlock
              block={block}
              basePath={basePath}
              canManage={canManage}
              selected={block.id === selectedBlockId}
              isFirstRow={isFirstRow}
              isLastRow={isLastRow}
              rowSize={blocks.length}
              onSelect={() => onSelectBlock(block.id)}
              onEdit={() => onEditBlock(block.id)}
              onMoveRowUp={() => onMoveRowUp(block.id)}
              onMoveRowDown={() => onMoveRowDown(block.id)}
              onDelete={() => onDeleteBlock(block.id)}
              onAddToRow={() => onAddToRow(block.id)}
              onUpdateContent={(content) => onUpdateBlockContent(block.id, content)}
            />
            {canManage && index < blocks.length - 1 ? (
              <Box
                onPointerDown={startResize(index)}
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: -4,
                  width: 8,
                  cursor: 'col-resize',
                  zIndex: 2,
                  touchAction: 'none',
                  '&:hover': { bgcolor: 'primary.main', opacity: 0.4 },
                }}
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize columns"
              />
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}
